// UC 34267 — System Setup & Master Data Page
// Mirrors RawHTMLDesign/system-administration-&-master-data/system-setup-&-master-data-page.html
// (Page Start / Page End block). Tabs are wired to live backend APIs under
// /api/master-data. All inputs / buttons carry data-testid for Playwright.
//
// Authorization: route is mounted behind ProtectedRoute(roles=[SuperAdmin,Finance]).
// Mutating buttons are also disabled for users without those roles as a defense
// in depth — backend enforces the same gate.

import { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api.js';
import { useAuth } from '../../../utils/AuthContext.jsx';
import { ROLES } from '../../../utils/roles.js';
import './SystemSetupAndMasterDataPage.css';

const ADMIN_ROLESS = [ROLES.SUPER_ADMIN, ROLES.FINANCE];

const TABS = [
  { id: 'brand', label: 'Brands & Outlets' },
  { id: 'station', label: 'Kitchen Stations' },
  { id: 'item', label: 'Ingredient / SKU Master' },
  { id: 'supplier', label: 'Supplier Master' },
  { id: 'user', label: 'Users & Access' },
  { id: 'cutoff', label: 'Cut-Off Rules' },
];

function FormBanner({ message, kind }) {
  if (!message) return null;
  return (
    <div
      className={`alert alert-${kind === 'error' ? 'danger' : 'success'} mt-3 mb-0`}
      data-testid={kind === 'error' ? 'form-error' : 'form-success'}
    >
      {message}
    </div>
  );
}

function useMasterDataLists() {
  const [brands, setBrands] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [stations, setStations] = useState([]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [cutoffs, setCutoffs] = useState([]);
  const [mgmtUsers, setMgmtUsers] = useState([]);

  async function refresh() {
    // Parallel fetch; tolerate per-endpoint 403 (e.g. lookup-only for admins).
    const calls = [
      api.get('/master-data/brands?includeInactive=true').then((r) => setBrands(r.data.data || [])).catch(() => {}),
      api.get('/master-data/outlets?includeInactive=true').then((r) => setOutlets(r.data.data || [])).catch(() => {}),
      api.get('/master-data/stations?includeInactive=true').then((r) => setStations(r.data.data || [])).catch(() => {}),
      api.get('/master-data/items?includeInactive=true').then((r) => setItems(r.data.data || [])).catch(() => {}),
      api.get('/master-data/suppliers?includeInactive=true').then((r) => setSuppliers(r.data.data || [])).catch(() => {}),
      api.get('/master-data/cutoff-rules').then((r) => setCutoffs(r.data.data || [])).catch(() => {}),
      api.get('/master-data/lookup/tenant-users').then((r) => setUsers(r.data.data || [])).catch(() => {}),
      api.get('/master-data/lookup/roles').then((r) => setRoles(r.data.data || [])).catch(() => {}),
      api.get('/master-data/user-assignments').then((r) => setAssignments(r.data.data || [])).catch(() => {}),
      api.get('/users').then((r) => setMgmtUsers(r.data.data || [])).catch(() => {}),
    ];
    await Promise.all(calls);
  }

  useEffect(() => { refresh(); }, []);

  return { brands, outlets, stations, items, suppliers, users, roles, assignments, cutoffs, mgmtUsers, refresh };
}

// ---------- Brand & Outlet section ----------
function BrandOutletSection({ canEdit, brands, outlets, onChanged }) {
  const [brandForm, setBrandForm] = useState({ name: '', code: '', notes: '' });
  const [outletForm, setOutletForm] = useState({ brand_id: '', name: '', code: '', status: 'Active' });
  const [banner, setBanner] = useState({ message: '', kind: '' });

  async function saveBrand(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    try {
      await api.post('/master-data/brands', brandForm);
      setBanner({ message: `Brand "${brandForm.name}" saved.`, kind: 'success' });
      setBrandForm({ name: '', code: '', notes: '' });
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  async function saveOutlet(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    try {
      await api.post('/master-data/outlets', outletForm);
      setBanner({ message: `Outlet "${outletForm.name}" saved.`, kind: 'success' });
      setOutletForm({ brand_id: outletForm.brand_id, name: '', code: '', status: 'Active' });
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  return (
    <div className="tab-pane fade show active" id="tab-brand" data-testid="tab-brand-panel">
      <div className="row">
        <div className="col-lg-7 mb-4">
          <div className="card h-100 form-card">
            <div className="card-header bg-white">
              <h2 className="section-title mb-1">Brand &amp; Outlet Setup</h2>
              <p className="text-muted small mb-0">Create brands, map outlets, and define operational cut-off windows.</p>
            </div>
            <div className="card-body">
              <form onSubmit={saveBrand} noValidate data-testid="brand-form">
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label htmlFor="brandName">Brand Name <span className="text-danger">*</span></label>
                    <input
                      id="brandName"
                      className="form-control"
                      value={brandForm.name}
                      onChange={(e) => setBrandForm({ ...brandForm, name: e.target.value })}
                      required
                      data-testid="brand-name"
                    />
                  </div>
                  <div className="form-group col-md-6">
                    <label htmlFor="brandCode">Brand Code <span className="text-danger">*</span></label>
                    <input
                      id="brandCode"
                      className="form-control"
                      maxLength={20}
                      value={brandForm.code}
                      onChange={(e) => setBrandForm({ ...brandForm, code: e.target.value.toUpperCase() })}
                      required
                      data-testid="brand-code"
                    />
                  </div>
                </div>
                <div className="form-group mb-3">
                  <label htmlFor="brandNotes">Notes</label>
                  <textarea
                    id="brandNotes"
                    className="form-control"
                    rows={2}
                    value={brandForm.notes}
                    onChange={(e) => setBrandForm({ ...brandForm, notes: e.target.value })}
                    data-testid="brand-notes"
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={!canEdit}
                  data-testid="save-brand"
                >
                  <i className="fa fa-floppy-disk mr-2" />Save Brand Setup
                </button>
                <FormBanner {...banner} />
              </form>

              <hr className="my-4" />

              <form onSubmit={saveOutlet} noValidate data-testid="outlet-form">
                <h6 className="font-weight-bold mb-2">Map an outlet to an existing brand</h6>
                <div className="form-row">
                  <div className="form-group col-md-4">
                    <label htmlFor="outletBrand">Brand <span className="text-danger">*</span></label>
                    <select
                      id="outletBrand"
                      className="form-control"
                      value={outletForm.brand_id}
                      onChange={(e) => setOutletForm({ ...outletForm, brand_id: e.target.value })}
                      required
                      data-testid="outlet-brand"
                    >
                      <option value="">Select brand</option>
                      {brands.map((b) => (
                        <option key={b.id} value={b.id}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="outletName">Outlet Name <span className="text-danger">*</span></label>
                    <input
                      id="outletName"
                      className="form-control"
                      value={outletForm.name}
                      onChange={(e) => setOutletForm({ ...outletForm, name: e.target.value })}
                      required
                      data-testid="outlet-name"
                    />
                  </div>
                  <div className="form-group col-md-2">
                    <label htmlFor="outletCode">Outlet Code <span className="text-danger">*</span></label>
                    <input
                      id="outletCode"
                      className="form-control"
                      value={outletForm.code}
                      onChange={(e) => setOutletForm({ ...outletForm, code: e.target.value.toUpperCase() })}
                      required
                      data-testid="outlet-code"
                    />
                  </div>
                  <div className="form-group col-md-3">
                    <label htmlFor="outletStatus">Status <span className="text-danger">*</span></label>
                    <select
                      id="outletStatus"
                      className="form-control"
                      value={outletForm.status}
                      onChange={(e) => setOutletForm({ ...outletForm, status: e.target.value })}
                      data-testid="outlet-status"
                    >
                      <option>Active</option>
                      <option>Inactive</option>
                      <option>Pending Go-Live</option>
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  className="btn btn-outline-secondary"
                  disabled={!canEdit}
                  data-testid="save-outlet"
                >
                  <i className="fa fa-link mr-2" />Save Outlet
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-5 mb-4">
          <div className="card h-100 insight-card">
            <div className="card-header bg-white">
              <h2 className="section-title mb-1">Current Brand Mapping</h2>
              <p className="text-muted small mb-0">Live data from the backend master-data API.</p>
            </div>
            <div className="card-body p-0">
              <div className="list-group list-group-flush compact-list" data-testid="brand-list">
                {brands.length === 0 && (
                  <div className="list-group-item text-muted small" data-testid="brand-list-empty">
                    No brands yet. Add one to get started.
                  </div>
                )}
                {brands.map((b) => {
                  const outletsForBrand = outlets.filter((o) => o.brand_id === b.id);
                  return (
                    <div
                      key={b.id}
                      className="list-group-item d-flex justify-content-between align-items-start"
                      data-testid={`brand-item-${b.code}`}
                    >
                      <div>
                        <strong>{b.name}</strong>
                        <div className="small text-muted">
                          {b.code} • {outletsForBrand.length} outlet{outletsForBrand.length === 1 ? '' : 's'}
                        </div>
                      </div>
                      <span className={`record-chip ${b.is_active ? 'chip-live' : 'chip-muted'}`}>
                        {b.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Kitchen Stations ----------
function StationSection({ canEdit, onChanged }) {
  const emptyForm = { name: '', station_type: '', availability_status: '', capacity_note: '' };
  const [form, setForm] = useState(emptyForm);
  const [banner, setBanner] = useState({ message: '', kind: '' });

  async function submit(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    if (!form.name.trim() || !form.station_type || !form.availability_status) {
      setBanner({ message: 'Station Name, Type, and Availability Status are required.', kind: 'error' });
      return;
    }
    try {
      await api.post('/master-data/stations', { ...form, availability_status: form.availability_status });
      setBanner({ message: `Station "${form.name}" saved.`, kind: 'success' });
      setForm(emptyForm);
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  return (
    <div className="tab-pane fade show active" data-testid="tab-station-panel">
      <div className="card form-card mb-4">
        <div className="card-header bg-white">
          <h2 className="section-title mb-1">Kitchen Station Setup</h2>
          <p className="text-muted small mb-0">Register production stations and define their capacity and availability.</p>
        </div>
        <div className="card-body">
          <form onSubmit={submit} noValidate data-testid="station-form">
            <div className="form-row">
              <div className="form-group col-md-4">
                <label htmlFor="stationName">Station Name <span className="text-danger">*</span></label>
                <input
                  id="stationName" className="form-control" placeholder="Oven 1"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required data-testid="station-name"
                />
              </div>
              <div className="form-group col-md-4">
                <label htmlFor="stationType">Station Type <span className="text-danger">*</span></label>
                <select id="stationType" className="form-control" value={form.station_type}
                  onChange={(e) => setForm({ ...form, station_type: e.target.value })}
                  required data-testid="station-type">
                  <option value="">Select type</option>
                  <option>Oven</option>
                  <option>Prep Bench</option>
                  <option>Chiller</option>
                  <option>Packaging Line</option>
                </select>
              </div>
              <div className="form-group col-md-4">
                <label htmlFor="availabilityStatus">Availability Status <span className="text-danger">*</span></label>
                <select id="availabilityStatus" className="form-control" value={form.availability_status}
                  onChange={(e) => setForm({ ...form, availability_status: e.target.value })}
                  required data-testid="station-availability">
                  <option value="">Select status</option>
                  <option>Available</option>
                  <option>Under Maintenance</option>
                  <option>Reserved</option>
                </select>
              </div>
            </div>
            <div className="form-row align-items-end">
              <div className="form-group col-md-8">
                <label htmlFor="capacityNote">Capacity Note</label>
                <input id="capacityNote" className="form-control" placeholder="Max 120 trays / shift"
                  value={form.capacity_note} onChange={(e) => setForm({ ...form, capacity_note: e.target.value })}
                  data-testid="station-capacity-note" />
              </div>
              <div className="form-group col-md-4 d-flex flex-wrap" style={{ gap: 8 }}>
                <button type="submit" className="btn btn-primary mb-2" disabled={!canEdit} data-testid="save-station">
                  <i className="fa fa-floppy-disk mr-2" />Save Station
                </button>
              </div>
            </div>
            <FormBanner {...banner} />
          </form>
        </div>
      </div>
    </div>
  );
}

// ---------- Items ----------
function ItemSection({ canEdit, items, onChanged }) {
  const [form, setForm] = useState({
    item_type: 'Ingredient', item_code: '', name: '', category: '', uom: '', is_active: true,
  });
  const [banner, setBanner] = useState({ message: '', kind: '' });

  async function submit(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    try {
      await api.post('/master-data/items', form);
      setBanner({ message: `Item "${form.item_code}" saved.`, kind: 'success' });
      setForm({ item_type: 'Ingredient', item_code: '', name: '', category: '', uom: '', is_active: true });
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  return (
    <div className="tab-pane fade show active" data-testid="tab-item-panel">
      <div className="card form-card mb-4">
        <div className="card-header bg-white">
          <h2 className="section-title mb-1">Ingredient &amp; SKU Master</h2>
        </div>
        <div className="card-body">
          <form onSubmit={submit} noValidate data-testid="item-form">
            <div className="form-row">
              <div className="form-group col-md-2">
                <label htmlFor="itemType">Type <span className="text-danger">*</span></label>
                <select id="itemType" className="form-control" value={form.item_type}
                  onChange={(e) => setForm({ ...form, item_type: e.target.value })}
                  data-testid="item-type"
                >
                  <option>Ingredient</option>
                  <option>SKU</option>
                </select>
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="itemCode">Code <span className="text-danger">*</span></label>
                <input id="itemCode" className="form-control" required
                  value={form.item_code}
                  onChange={(e) => setForm({ ...form, item_code: e.target.value.toUpperCase() })}
                  data-testid="item-code"
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="itemName">Name <span className="text-danger">*</span></label>
                <input id="itemName" className="form-control" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="item-name"
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="itemCategory">Category <span className="text-danger">*</span></label>
                <select id="itemCategory" className="form-control" required
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  data-testid="item-category"
                >
                  <option value="">Select</option>
                  <option>Protein</option>
                  <option>Dry Goods</option>
                  <option>Packaging</option>
                  <option>Finished Goods</option>
                  <option>Vegetables</option>
                </select>
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="uom">UoM <span className="text-danger">*</span></label>
                <select id="uom" className="form-control" required
                  value={form.uom}
                  onChange={(e) => setForm({ ...form, uom: e.target.value })}
                  data-testid="item-uom"
                >
                  <option value="">Select</option>
                  <option>kg</option>
                  <option>g</option>
                  <option>pc</option>
                  <option>ltr</option>
                </select>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={!canEdit} data-testid="save-item">
              <i className="fa fa-floppy-disk mr-2" />Save Item Master
            </button>
            <FormBanner {...banner} />
          </form>

          <div className="mt-4" data-testid="item-list">
            <h6 className="font-weight-bold">Catalog ({items.length})</h6>
            <ul className="list-group">
              {items.map((it) => (
                <li key={it.id} className="list-group-item d-flex justify-content-between" data-testid={`item-row-${it.item_code}`}>
                  <span><strong>{it.item_code}</strong> — {it.name}</span>
                  <span className="text-muted small">{it.item_type} • {it.category} • {it.uom}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Suppliers ----------
function SupplierSection({ canEdit, suppliers, onChanged }) {
  const [form, setForm] = useState({
    name: '', supplier_code: '', contact_person: '', contact_number: '', email: '',
    supplied_categories: [],
  });
  const [banner, setBanner] = useState({ message: '', kind: '' });

  function toggleCategory(cat) {
    setForm((f) => ({
      ...f,
      supplied_categories: f.supplied_categories.includes(cat)
        ? f.supplied_categories.filter((c) => c !== cat)
        : [...f.supplied_categories, cat],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    try {
      await api.post('/master-data/suppliers', form);
      setBanner({ message: `Supplier "${form.name}" saved.`, kind: 'success' });
      setForm({
        name: '', supplier_code: '', contact_person: '', contact_number: '', email: '',
        supplied_categories: [],
      });
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  return (
    <div className="tab-pane fade show active" data-testid="tab-supplier-panel">
      <div className="card form-card mb-4">
        <div className="card-header bg-white"><h2 className="section-title mb-1">Supplier Master</h2></div>
        <div className="card-body">
          <form onSubmit={submit} noValidate data-testid="supplier-form">
            <div className="form-row">
              <div className="form-group col-md-3">
                <label htmlFor="supplierName">Supplier Name <span className="text-danger">*</span></label>
                <input id="supplierName" className="form-control" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="supplier-name"
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="supplierCode">Supplier Code <span className="text-danger">*</span></label>
                <input id="supplierCode" className="form-control" required
                  value={form.supplier_code}
                  onChange={(e) => setForm({ ...form, supplier_code: e.target.value.toUpperCase() })}
                  data-testid="supplier-code"
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="contactPerson">Contact Person</label>
                <input id="contactPerson" className="form-control"
                  value={form.contact_person}
                  onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
                  data-testid="supplier-contact"
                />
              </div>
              <div className="form-group col-md-2">
                <label htmlFor="contactNumber">Contact Number</label>
                <input id="contactNumber" className="form-control"
                  value={form.contact_number}
                  onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
                  data-testid="supplier-phone"
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="supplierEmail">Email</label>
                <input id="supplierEmail" type="email" className="form-control"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="supplier-email"
                />
              </div>
            </div>
            <div className="form-group">
              <label>Supplied Categories</label>
              <div data-testid="supplier-categories">
                {['Protein', 'Vegetables', 'Dry Goods', 'Packaging'].map((cat) => (
                  <label key={cat} className="mr-3 small">
                    <input
                      type="checkbox"
                      checked={form.supplied_categories.includes(cat)}
                      onChange={() => toggleCategory(cat)}
                      data-testid={`supplier-cat-${cat.replace(/\s+/g, '-')}`}
                    /> {cat}
                  </label>
                ))}
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={!canEdit} data-testid="save-supplier">
              <i className="fa fa-floppy-disk mr-2" />Save Supplier
            </button>
            <FormBanner {...banner} />
          </form>

          <div className="mt-4" data-testid="supplier-list">
            <h6 className="font-weight-bold">Approved Suppliers ({suppliers.length})</h6>
            <ul className="list-group">
              {suppliers.map((s) => (
                <li key={s.id} className="list-group-item d-flex justify-content-between" data-testid={`supplier-row-${s.supplier_code}`}>
                  <span><strong>{s.supplier_code}</strong> — {s.name}</span>
                  <span className="text-muted small">{(s.supplied_categories || []).join(', ')}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- User Management ----------
function UserManagementSection({ canEdit, mgmtUsers, onChanged }) {
  const emptyForm = { email: '', full_name: '', password: '' };
  const [form, setForm] = useState(emptyForm);
  const [banner, setBanner] = useState({ message: '', kind: '' });
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({});
  const [pwdUserId, setPwdUserId] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [pwdBanner, setPwdBanner] = useState({ message: '', kind: '' });

  async function createUser(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    try {
      await api.post('/users', form);
      setBanner({ message: `User "${form.full_name}" created.`, kind: 'success' });
      setForm(emptyForm);
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Create failed', kind: 'error' });
    }
  }

  async function saveEdit(id) {
    try {
      await api.put(`/users/${id}`, editData);
      setEditId(null);
      await onChanged();
    } catch (err) {
      alert(err?.response?.data?.error || 'Update failed');
    }
  }

  async function toggleActive(user) {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      await onChanged();
    } catch (err) {
      alert(err?.response?.data?.error || 'Update failed');
    }
  }

  async function resetPassword(e) {
    e.preventDefault();
    setPwdBanner({ message: '', kind: '' });
    try {
      await api.post(`/users/${pwdUserId}/reset-password`, { password: newPwd });
      setPwdBanner({ message: 'Password reset.', kind: 'success' });
      setNewPwd('');
    } catch (err) {
      setPwdBanner({ message: err?.response?.data?.error || 'Reset failed', kind: 'error' });
    }
  }

  const ROLE_COLORS = {
    SuperAdmin: '#ef4444', Finance: '#f59e0b', BrandManager: '#60a5fa',
    CommissarySupervisor: '#a78bfa', OutletManager: '#34d399', ProcurementStaff: '#fb923c',
  };

  return (
    <div className="card form-card mb-4" data-testid="user-mgmt-section">
      <div className="card-header bg-white d-flex align-items-center justify-content-between">
        <h2 className="section-title mb-0">User Management</h2>
        <span className="badge badge-secondary">{mgmtUsers.length} users</span>
      </div>
      <div className="card-body">
        {/* Create user form */}
        {canEdit && (
          <form onSubmit={createUser} noValidate className="mb-4" data-testid="create-user-form">
            <h6 className="font-weight-bold mb-2">Create New User</h6>
            <div className="form-row">
              <div className="form-group col-md-3">
                <label htmlFor="newFullName">Full Name <span className="text-danger">*</span></label>
                <input id="newFullName" className="form-control" required
                  value={form.full_name}
                  onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                  placeholder="Jane Dela Cruz"
                  data-testid="create-user-name"
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="newEmail">Email <span className="text-danger">*</span></label>
                <input id="newEmail" type="email" className="form-control" required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="jane@company.com"
                  data-testid="create-user-email"
                />
              </div>
              <div className="form-group col-md-3">
                <label htmlFor="newPwd">Initial Password <span className="text-danger">*</span></label>
                <input id="newPwd" type="password" className="form-control" required
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Min 8 characters"
                  data-testid="create-user-password"
                />
              </div>
              <div className="form-group col-md-2 d-flex align-items-end">
                <button type="submit" className="btn btn-primary w-100" data-testid="create-user-submit">
                  <i className="fa fa-user-plus mr-1" />Create
                </button>
              </div>
            </div>
            <FormBanner {...banner} />
          </form>
        )}

        {/* Users table */}
        <div data-testid="user-mgmt-table">
          <h6 className="font-weight-bold mb-2">All Users</h6>
          <div className="table-responsive">
            <table className="table table-sm table-hover">
              <thead className="thead-light">
                <tr>
                  <th>Name</th><th>Email</th><th>Role</th><th>Brands</th><th>Outlets</th><th>Status</th>
                  {canEdit && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {mgmtUsers.map((u) => {
                  const roleColor = ROLE_COLORS[u.role] || '#94a3b8';
                  return editId === u.id ? (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td><input className="form-control form-control-sm" value={editData.full_name || ''}
                        onChange={(e) => setEditData({ ...editData, full_name: e.target.value })} /></td>
                      <td><input className="form-control form-control-sm" value={editData.email || ''}
                        onChange={(e) => setEditData({ ...editData, email: e.target.value })} /></td>
                      <td colSpan={2}></td>
                      <td></td>
                      <td></td>
                      <td>
                        <button className="btn btn-sm btn-success mr-1" onClick={() => saveEdit(u.id)} data-testid={`save-user-${u.id}`}>Save</button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditId(null)}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={u.id} data-testid={`user-row-${u.id}`}>
                      <td className="font-weight-medium">{u.full_name}</td>
                      <td className="text-muted small">{u.email}</td>
                      <td>
                        {u.role
                          ? <span className="badge" style={{ background: roleColor + '22', color: roleColor, padding: '3px 8px', borderRadius: 100 }}>{u.role}</span>
                          : <span className="text-muted small">—</span>}
                      </td>
                      <td className="text-center">{u.brand_count || 0}</td>
                      <td className="text-center">{u.outlet_count || 0}</td>
                      <td>
                        <span className={`badge badge-${u.is_active ? 'success' : 'secondary'}`}>
                          {u.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      {canEdit && (
                        <td className="text-nowrap">
                          <button className="btn btn-xs btn-outline-primary mr-1"
                            onClick={() => { setEditId(u.id); setEditData({ full_name: u.full_name, email: u.email }); }}
                            data-testid={`edit-user-${u.id}`} title="Edit">
                            <i className="fa fa-pen" />
                          </button>
                          <button className="btn btn-xs btn-outline-warning mr-1"
                            onClick={() => { setPwdUserId(u.id); setNewPwd(''); setPwdBanner({}); }}
                            data-testid={`reset-pwd-${u.id}`} title="Reset password">
                            <i className="fa fa-key" />
                          </button>
                          <button className={`btn btn-xs btn-outline-${u.is_active ? 'danger' : 'success'}`}
                            onClick={() => toggleActive(u)}
                            data-testid={`toggle-active-${u.id}`}
                            title={u.is_active ? 'Deactivate' : 'Activate'}>
                            <i className={`fa fa-${u.is_active ? 'user-slash' : 'user-check'}`} />
                          </button>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Reset password inline panel */}
        {pwdUserId && (
          <div className="card border-warning mt-3" data-testid="reset-pwd-panel">
            <div className="card-body">
              <h6 className="font-weight-bold mb-2">
                <i className="fa fa-key mr-2 text-warning" />
                Reset Password — {mgmtUsers.find((u) => u.id === pwdUserId)?.email}
              </h6>
              <form onSubmit={resetPassword} noValidate className="form-inline">
                <input type="password" className="form-control form-control-sm mr-2"
                  value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                  placeholder="New password (min 8 chars)" style={{ width: 240 }}
                  data-testid="new-password-input"
                />
                <button type="submit" className="btn btn-sm btn-warning mr-2" data-testid="confirm-reset-pwd">
                  Set Password
                </button>
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setPwdUserId(null)}>
                  Cancel
                </button>
              </form>
              <FormBanner {...pwdBanner} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- User Assignments ----------
function UserAssignmentSection({ canEdit, users, roles, brands, outlets, assignments, onChanged }) {
  const [form, setForm] = useState({ user_id: '', role_id: '', brand_ids: [], outlet_ids: [] });
  const [banner, setBanner] = useState({ message: '', kind: '' });

  function toggleArray(field, id) {
    setForm((f) => ({
      ...f,
      [field]: f[field].includes(id) ? f[field].filter((x) => x !== id) : [...f[field], id],
    }));
  }

  async function submit(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    try {
      await api.post('/master-data/user-assignments', {
        ...form,
        role_id: Number(form.role_id),
      });
      setBanner({ message: 'Role assigned.', kind: 'success' });
      setForm({ user_id: '', role_id: '', brand_ids: [], outlet_ids: [] });
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  return (
    <div className="card form-card mb-4">
      <div className="card-header bg-white"><h2 className="section-title mb-1">Role Assignment</h2></div>
        <div className="card-body">
          <form onSubmit={submit} noValidate data-testid="user-assignment-form">
            <div className="form-row">
              <div className="form-group col-md-4">
                <label htmlFor="userSelect">User <span className="text-danger">*</span></label>
                <select id="userSelect" className="form-control" required
                  value={form.user_id}
                  onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                  data-testid="assignment-user"
                >
                  <option value="">Select user</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>
                  ))}
                </select>
              </div>
              <div className="form-group col-md-4">
                <label htmlFor="roleSelect">Role <span className="text-danger">*</span></label>
                <select id="roleSelect" className="form-control" required
                  value={form.role_id}
                  onChange={(e) => setForm({ ...form, role_id: e.target.value })}
                  data-testid="assignment-role"
                >
                  <option value="">Select role</option>
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group col-md-6">
                <label>Brand Access</label>
                <div data-testid="assignment-brands">
                  {brands.map((b) => (
                    <label key={b.id} className="mr-3 small d-inline-flex align-items-center">
                      <input type="checkbox" className="mr-1"
                        checked={form.brand_ids.includes(b.id)}
                        onChange={() => toggleArray('brand_ids', b.id)}
                        data-testid={`assignment-brand-${b.code}`}
                      /> {b.name}
                    </label>
                  ))}
                </div>
              </div>
              <div className="form-group col-md-6">
                <label>Outlet Access</label>
                <div data-testid="assignment-outlets">
                  {outlets.map((o) => (
                    <label key={o.id} className="mr-3 small d-inline-flex align-items-center">
                      <input type="checkbox" className="mr-1"
                        checked={form.outlet_ids.includes(o.id)}
                        onChange={() => toggleArray('outlet_ids', o.id)}
                        data-testid={`assignment-outlet-${o.code}`}
                      /> {o.name}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <button type="submit" className="btn btn-primary" disabled={!canEdit} data-testid="save-user-assignment">
              <i className="fa fa-user-check mr-2" />Assign Role
            </button>
            <FormBanner {...banner} />
          </form>

          <div className="mt-4" data-testid="assignment-list">
            <h6 className="font-weight-bold">Current Assignments ({assignments.length})</h6>
            <table className="table table-sm">
              <thead>
                <tr><th>User</th><th>Role</th><th>Brands</th><th>Outlets</th></tr>
              </thead>
              <tbody>
                {assignments.map((a) => (
                  <tr key={a.id} data-testid={`assignment-row-${a.id}`}>
                    <td>{a.user_name} <span className="text-muted small">({a.user_email})</span></td>
                    <td>{a.role_name}</td>
                    <td>{(a.brand_ids || []).length}</td>
                    <td>{(a.outlet_ids || []).length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
  );
}

// ---------- Cut-off Rules ----------
function CutoffRuleSection({ canEdit, brands, cutoffs, onChanged }) {
  const [form, setForm] = useState({
    brand_id: '', cutoff_time: '17:00', cross_midnight: false,
    late_warning_rule: 'Warn Only', helper_text: '',
  });
  const [banner, setBanner] = useState({ message: '', kind: '' });

  async function submit(e) {
    e.preventDefault();
    setBanner({ message: '', kind: '' });
    try {
      await api.post('/master-data/cutoff-rules', form);
      setBanner({ message: 'Cut-off rule saved.', kind: 'success' });
      setForm({ brand_id: '', cutoff_time: '17:00', cross_midnight: false, late_warning_rule: 'Warn Only', helper_text: '' });
      await onChanged();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Save failed', kind: 'error' });
    }
  }

  return (
    <div className="tab-pane fade show active" data-testid="tab-cutoff-panel">
      <div className="card form-card mb-4">
        <div className="card-header bg-white"><h2 className="section-title mb-1">Cut-Off Rules</h2></div>
        <div className="card-body">
          <form onSubmit={submit} noValidate data-testid="cutoff-form">
            <div className="form-row align-items-end">
              <div className="col-md-3 form-group">
                <label htmlFor="ruleBrand">Brand <span className="text-danger">*</span></label>
                <select id="ruleBrand" className="form-control" required
                  value={form.brand_id}
                  onChange={(e) => setForm({ ...form, brand_id: e.target.value })}
                  data-testid="cutoff-brand"
                >
                  <option value="">Select brand</option>
                  {brands.map((b) => (<option key={b.id} value={b.id}>{b.name}</option>))}
                </select>
              </div>
              <div className="col-md-2 form-group">
                <label htmlFor="ruleTime">Cut-Off Time <span className="text-danger">*</span></label>
                <input id="ruleTime" type="time" className="form-control"
                  value={form.cutoff_time}
                  onChange={(e) => setForm({ ...form, cutoff_time: e.target.value })}
                  data-testid="cutoff-time"
                />
              </div>
              <div className="col-md-3 form-group">
                <label htmlFor="lateWarning">Late Warning Rule</label>
                <select id="lateWarning" className="form-control"
                  value={form.late_warning_rule}
                  onChange={(e) => setForm({ ...form, late_warning_rule: e.target.value })}
                  data-testid="cutoff-warning-rule"
                >
                  <option>Warn Only</option>
                  <option>Warn + Supervisor Approval</option>
                  <option>Block Submission</option>
                </select>
              </div>
              <div className="col-md-2 form-group">
                <label htmlFor="crossMid" className="d-block">Cross-midnight</label>
                <input id="crossMid" type="checkbox"
                  checked={form.cross_midnight}
                  onChange={(e) => setForm({ ...form, cross_midnight: e.target.checked })}
                  data-testid="cutoff-cross-midnight"
                />
              </div>
              <div className="col-md-2 form-group">
                <button type="submit" className="btn btn-primary" disabled={!canEdit} data-testid="save-cutoff">
                  <i className="fa fa-floppy-disk mr-2" />Save Rule
                </button>
              </div>
            </div>
            <div className="form-group mb-0">
              <label htmlFor="helperText" className="small">Helper text (shown on pull-order pages)</label>
              <input id="helperText" className="form-control"
                value={form.helper_text}
                onChange={(e) => setForm({ ...form, helper_text: e.target.value })}
                data-testid="cutoff-helper"
              />
            </div>
            <FormBanner {...banner} />
          </form>

          <div className="mt-4" data-testid="cutoff-list">
            <h6 className="font-weight-bold">Configured Rules ({cutoffs.length})</h6>
            <ul className="list-group">
              {cutoffs.map((c) => (
                <li key={c.id} className="list-group-item d-flex justify-content-between" data-testid={`cutoff-row-${c.brand_id}`}>
                  <span><strong>{c.brand_name}</strong> — {c.cutoff_time} {c.cross_midnight ? '(next day)' : ''}</span>
                  <span className="text-muted small">{c.late_warning_rule}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Master Data Summary ----------
function MasterDataSummary({ brands, outlets, items, suppliers, stations, cutoffs, onChanged }) {
  const [domainFilter, setDomainFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [historyRecord, setHistoryRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);
  const PAGE_SIZE = 8;

  // Build unified record list from all domains
  const allRecords = useMemo(() => {
    const rows = [];

    brands.forEach((b) => rows.push({
      id: b.id, domain: 'brand', domainLabel: 'Brands & Outlets',
      name: b.name, code: b.code,
      status: b.is_active ? 'Active' : 'Inactive',
      updatedBy: '—', updatedAt: b.updated_at || b.created_at, raw: b,
    }));

    outlets.forEach((o) => rows.push({
      id: o.id, domain: 'outlet', domainLabel: 'Brands & Outlets',
      name: o.name, code: o.code,
      status: o.is_active !== false ? 'Active' : 'Inactive',
      updatedBy: '—', updatedAt: o.updated_at || o.created_at, raw: o,
    }));

    stations.forEach((s) => rows.push({
      id: s.id, domain: 'station', domainLabel: 'Kitchen Stations',
      name: s.name, code: s.station_type || '—',
      status: s.is_active !== false ? 'Active' : 'Inactive',
      updatedBy: '—', updatedAt: s.updated_at || s.created_at, raw: s,
    }));

    items.forEach((i) => rows.push({
      id: i.id, domain: 'item', domainLabel: 'Ingredient / SKU',
      name: i.name, code: i.item_code,
      status: i.is_active !== false ? 'Active' : 'Inactive',
      updatedBy: '—', updatedAt: i.updated_at || i.created_at, raw: i,
    }));

    suppliers.forEach((s) => rows.push({
      id: s.id, domain: 'supplier', domainLabel: 'Suppliers',
      name: s.name, code: s.supplier_code,
      status: s.is_active !== false ? 'Active' : 'Inactive',
      updatedBy: '—', updatedAt: s.updated_at || s.created_at, raw: s,
    }));

    cutoffs.forEach((c) => rows.push({
      id: c.id, domain: 'cutoff', domainLabel: 'Cut-Off Rules',
      name: `${c.brand_name} — ${c.cutoff_time}`, code: c.brand_code || '—',
      status: 'Active',
      updatedBy: '—', updatedAt: c.updated_at || c.created_at, raw: c,
    }));

    return rows;
  }, [brands, outlets, stations, items, suppliers, cutoffs]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return allRecords.filter((r) => {
      if (domainFilter !== 'all' && r.domain !== domainFilter) return false;
      if (statusFilter === 'active' && r.status !== 'Active') return false;
      if (statusFilter === 'inactive' && r.status !== 'Inactive') return false;
      if (term && !`${r.name} ${r.code}`.toLowerCase().includes(term)) return false;
      return true;
    });
  }, [allRecords, domainFilter, statusFilter, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  function fmtDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('en-US', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  }

  function statusChip(status) {
    if (status === 'Active') return <span className="record-chip chip-live">Active</span>;
    if (status === 'Needs Review') return <span className="record-chip chip-warning">Needs Review</span>;
    return <span className="record-chip chip-muted">Inactive</span>;
  }

  function openEdit(r) {
    const f = { ...r.raw };
    // Normalize is_active to boolean for all domains
    if (r.domain === 'brand') f._isActive = r.raw.is_active !== false;
    if (r.domain === 'outlet') f._isActive = r.raw.is_active !== false;
    if (r.domain === 'station') f._isActive = r.raw.is_active !== false;
    if (r.domain === 'item') {
      f._isActive = r.raw.is_active !== false;
      // Surface the price with 2 decimal places (the column stores scale-4).
      f.standard_unit_price =
        r.raw.standard_unit_price != null && r.raw.standard_unit_price !== ''
          ? Number(r.raw.standard_unit_price).toFixed(2)
          : '';
    }
    if (r.domain === 'supplier') f._isActive = r.raw.is_active !== false;
    setEditRecord(r);
    setEditForm(f);
    setEditError('');
  }

  async function submitEdit() {
    setEditError('');
    setEditSubmitting(true);
    try {
      const r = editRecord;
      const f = editForm;
      const endpointMap = {
        brand: '/master-data/brands',
        outlet: '/master-data/outlets',
        station: '/master-data/stations',
        item: '/master-data/items',
        supplier: '/master-data/suppliers',
        cutoff: '/master-data/cutoff-rules',
      };
      const base = endpointMap[r.domain];
      let payload = {};
      if (r.domain === 'brand') {
        payload = { name: f.name, code: f.code, notes: f.notes, is_active: f._isActive };
      } else if (r.domain === 'outlet') {
        payload = { name: f.name, code: f.code, status: f._isActive ? 'Active' : 'Inactive' };
      } else if (r.domain === 'station') {
        payload = { name: f.name, station_type: f.station_type, availability_status: f.availability_status, capacity_note: f.capacity_note, is_active: f._isActive };
      } else if (r.domain === 'item') {
        payload = { name: f.name, category: f.category, uom: f.uom, standard_unit_price: f.standard_unit_price, is_active: f._isActive };
      } else if (r.domain === 'supplier') {
        payload = { name: f.name, contact_person: f.contact_person, contact_number: f.contact_number, email: f.email, is_active: f._isActive };
      } else if (r.domain === 'cutoff') {
        payload = { cutoff_time: f.cutoff_time, cross_midnight: f.cross_midnight, late_warning_rule: f.late_warning_rule, helper_text: f.helper_text };
      }
      await api.patch(`${base}/${r.id}`, payload);
      setEditRecord(null);
      if (onChanged) await onChanged();
    } catch (err) {
      setEditError(err?.response?.data?.error || 'Failed to save changes.');
    } finally {
      setEditSubmitting(false);
    }
  }

  return (
    <>
      <div className="card summary-card" data-testid="master-data-summary">
        <div className="card-header bg-white d-flex flex-column flex-lg-row justify-content-between align-items-lg-center">
          <div>
            <h2 className="section-title mb-1">Master Data Summary</h2>
            <p className="text-muted small mb-0">Search, filter, edit, and review change history across all setup domains.</p>
          </div>
          <div className="d-flex flex-wrap mt-3 mt-lg-0" style={{ gap: 8 }}>
            <select className="form-control form-control-sm" style={{ minWidth: 140 }}
              value={domainFilter} onChange={(e) => { setDomainFilter(e.target.value); setPage(1); }}
              data-testid="summary-domain-filter">
              <option value="all">All Domains</option>
              <option value="brand">Brands &amp; Outlets</option>
              <option value="station">Kitchen Stations</option>
              <option value="item">Ingredient / SKU</option>
              <option value="supplier">Suppliers</option>
              <option value="cutoff">Cut-Off Rules</option>
            </select>
            <select className="form-control form-control-sm" style={{ minWidth: 120 }}
              value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              data-testid="summary-status-filter">
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <input type="search" className="form-control form-control-sm" style={{ minWidth: 200 }}
              placeholder="Search record name or code"
              value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              data-testid="summary-search" />
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover summary-table mb-0" data-testid="summary-table">
              <thead>
                <tr>
                  <th>Domain Type</th>
                  <th>Record Name</th>
                  <th>Code / Reference</th>
                  <th>Status</th>
                  <th>Last Updated By</th>
                  <th>Last Updated At</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted py-4" data-testid="summary-empty">No matching records found.</td></tr>
                )}
                {pageRows.map((r, i) => (
                  <tr key={i} data-testid={`summary-row-${i}`}>
                    <td className="text-muted small">{r.domainLabel}</td>
                    <td className="font-weight-600">{r.name}</td>
                    <td className="text-muted small">{r.code}</td>
                    <td>{statusChip(r.status)}</td>
                    <td className="text-muted small">{r.updatedBy}</td>
                    <td className="text-muted small">{fmtDate(r.updatedAt)}</td>
                    <td className="text-right text-nowrap">
                      <button className="btn btn-link btn-sm p-0 mr-2" style={{ color: '#008080' }}
                        onClick={() => openEdit(r)} data-testid={`summary-edit-${i}`}>Edit</button>
                      <button className="btn btn-link btn-sm p-0" style={{ color: '#008080' }}
                        onClick={() => setHistoryRecord(r)} data-testid={`summary-history-${i}`}>View History</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer bg-white d-flex flex-column flex-md-row justify-content-between align-items-md-center">
          <small className="text-muted mb-2 mb-md-0" data-testid="summary-count">
            Showing {filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length} record{filtered.length !== 1 ? 's' : ''}
          </small>
          <nav aria-label="Summary pagination">
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>Previous</button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <li key={p} className={`page-item ${p === page ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => setPage(p)}>{p}</button>
                </li>
              ))}
              <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>Next</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Edit Modal */}
      {editRecord && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }} data-testid="summary-edit-modal">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header" style={{ background: '#008080', color: '#fff', borderRadius: '14px 14px 0 0' }}>
                <h5 className="modal-title font-weight-bold">
                  <i className="fa fa-pen-to-square mr-2" />Edit {editRecord.domainLabel} — {editRecord.name}
                </h5>
                <button type="button" className="close" style={{ color: '#fff', opacity: 1 }} onClick={() => setEditRecord(null)}><span>&times;</span></button>
              </div>
              <div className="modal-body p-4">
                {editError && <div className="alert alert-danger py-2" data-testid="summary-edit-error">{editError}</div>}

                {/* BRAND */}
                {editRecord.domain === 'brand' && (
                  <>
                    <div className="form-row">
                      <div className="form-group col-md-7">
                        <label className="font-weight-600">Brand Name <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-brand-name" />
                      </div>
                      <div className="form-group col-md-5">
                        <label className="font-weight-600">Brand Code <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" value={editForm.code || ''} onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))} data-testid="edit-brand-code" />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="font-weight-600">Notes</label>
                      <textarea className="form-control" rows="2" value={editForm.notes || ''} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))} />
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-switch">
                        <input type="checkbox" className="custom-control-input" id="edit-brand-active" checked={!!editForm._isActive} onChange={(e) => setEditForm((f) => ({ ...f, _isActive: e.target.checked }))} data-testid="edit-brand-active" />
                        <label className="custom-control-label" htmlFor="edit-brand-active">Active</label>
                      </div>
                    </div>
                  </>
                )}

                {/* OUTLET */}
                {editRecord.domain === 'outlet' && (
                  <>
                    <div className="form-row">
                      <div className="form-group col-md-7">
                        <label className="font-weight-600">Outlet Name <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-outlet-name" />
                      </div>
                      <div className="form-group col-md-5">
                        <label className="font-weight-600">Outlet Code <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" value={editForm.code || ''} onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))} data-testid="edit-outlet-code" />
                      </div>
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-switch">
                        <input type="checkbox" className="custom-control-input" id="edit-outlet-active" checked={!!editForm._isActive} onChange={(e) => setEditForm((f) => ({ ...f, _isActive: e.target.checked }))} data-testid="edit-outlet-active" />
                        <label className="custom-control-label" htmlFor="edit-outlet-active">Active</label>
                      </div>
                    </div>
                  </>
                )}

                {/* STATION */}
                {editRecord.domain === 'station' && (
                  <>
                    <div className="form-row">
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Station Name <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-station-name" />
                      </div>
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Station Type <span className="text-danger">*</span></label>
                        <select className="form-control custom-select" value={editForm.station_type || ''} onChange={(e) => setEditForm((f) => ({ ...f, station_type: e.target.value }))} data-testid="edit-station-type">
                          <option value="" disabled>Select type</option>
                          {['Bakery', 'Butchery', 'Cold Kitchen', 'Hot Kitchen', 'Packaging', 'Pastry', 'Prep', 'Storage'].map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Availability Status</label>
                        <select className="form-control custom-select" value={editForm.availability_status || ''} onChange={(e) => setEditForm((f) => ({ ...f, availability_status: e.target.value }))} data-testid="edit-station-availability">
                          <option value="Available">Available</option>
                          <option value="Occupied">Occupied</option>
                          <option value="Under Maintenance">Under Maintenance</option>
                        </select>
                      </div>
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Capacity Note</label>
                        <input type="text" className="form-control" value={editForm.capacity_note || ''} onChange={(e) => setEditForm((f) => ({ ...f, capacity_note: e.target.value }))} />
                      </div>
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-switch">
                        <input type="checkbox" className="custom-control-input" id="edit-station-active" checked={!!editForm._isActive} onChange={(e) => setEditForm((f) => ({ ...f, _isActive: e.target.checked }))} data-testid="edit-station-active" />
                        <label className="custom-control-label" htmlFor="edit-station-active">Active</label>
                      </div>
                    </div>
                  </>
                )}

                {/* ITEM */}
                {editRecord.domain === 'item' && (
                  <>
                    <div className="form-row">
                      <div className="form-group col-md-8">
                        <label className="font-weight-600">Item Name <span className="text-danger">*</span></label>
                        <input type="text" className="form-control" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-item-name" />
                      </div>
                      <div className="form-group col-md-4">
                        <label className="font-weight-600">UOM</label>
                        <input type="text" className="form-control" value={editForm.uom || ''} onChange={(e) => setEditForm((f) => ({ ...f, uom: e.target.value }))} data-testid="edit-item-uom" />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Category</label>
                        <input type="text" className="form-control" value={editForm.category || ''} onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))} />
                      </div>
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Standard Unit Price</label>
                        <input type="number" className="form-control" min="0" step="0.01" value={editForm.standard_unit_price ?? ''} onChange={(e) => setEditForm((f) => ({ ...f, standard_unit_price: e.target.value }))} onBlur={(e) => { const v = e.target.value; if (v !== '' && !Number.isNaN(Number(v))) setEditForm((f) => ({ ...f, standard_unit_price: Number(v).toFixed(2) })); }} />
                      </div>
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-switch">
                        <input type="checkbox" className="custom-control-input" id="edit-item-active" checked={!!editForm._isActive} onChange={(e) => setEditForm((f) => ({ ...f, _isActive: e.target.checked }))} data-testid="edit-item-active" />
                        <label className="custom-control-label" htmlFor="edit-item-active">Active</label>
                      </div>
                    </div>
                  </>
                )}

                {/* SUPPLIER */}
                {editRecord.domain === 'supplier' && (
                  <>
                    <div className="form-group">
                      <label className="font-weight-600">Supplier Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" value={editForm.name || ''} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} data-testid="edit-supplier-name" />
                    </div>
                    <div className="form-row">
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Contact Person</label>
                        <input type="text" className="form-control" value={editForm.contact_person || ''} onChange={(e) => setEditForm((f) => ({ ...f, contact_person: e.target.value }))} />
                      </div>
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Contact Number</label>
                        <input type="text" className="form-control" value={editForm.contact_number || ''} onChange={(e) => setEditForm((f) => ({ ...f, contact_number: e.target.value }))} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="font-weight-600">Email</label>
                      <input type="email" className="form-control" value={editForm.email || ''} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} />
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-switch">
                        <input type="checkbox" className="custom-control-input" id="edit-supplier-active" checked={!!editForm._isActive} onChange={(e) => setEditForm((f) => ({ ...f, _isActive: e.target.checked }))} data-testid="edit-supplier-active" />
                        <label className="custom-control-label" htmlFor="edit-supplier-active">Active</label>
                      </div>
                    </div>
                  </>
                )}

                {/* CUTOFF RULE */}
                {editRecord.domain === 'cutoff' && (
                  <>
                    <div className="form-row">
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Cut-Off Time <span className="text-danger">*</span></label>
                        <input type="time" className="form-control" value={editForm.cutoff_time || ''} onChange={(e) => setEditForm((f) => ({ ...f, cutoff_time: e.target.value }))} data-testid="edit-cutoff-time" />
                      </div>
                      <div className="form-group col-md-6">
                        <label className="font-weight-600">Late Warning Rule</label>
                        <select className="form-control custom-select" value={editForm.late_warning_rule || 'Warn Only'} onChange={(e) => setEditForm((f) => ({ ...f, late_warning_rule: e.target.value }))} data-testid="edit-cutoff-warning">
                          <option value="Warn Only">Warn Only</option>
                          <option value="Block Submission">Block Submission</option>
                        </select>
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="font-weight-600">Helper Text</label>
                      <input type="text" className="form-control" value={editForm.helper_text || ''} onChange={(e) => setEditForm((f) => ({ ...f, helper_text: e.target.value }))} />
                    </div>
                    <div className="form-group mb-0">
                      <div className="custom-control custom-switch">
                        <input type="checkbox" className="custom-control-input" id="edit-cutoff-midnight" checked={!!editForm.cross_midnight} onChange={(e) => setEditForm((f) => ({ ...f, cross_midnight: e.target.checked }))} data-testid="edit-cutoff-midnight" />
                        <label className="custom-control-label" htmlFor="edit-cutoff-midnight">Crosses Midnight</label>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setEditRecord(null)}>Cancel</button>
                <button type="button" className="btn btn-teal" onClick={submitEdit} disabled={editSubmitting} data-testid="btn-summary-save">
                  <i className="fa fa-floppy-disk mr-1" />{editSubmitting ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {historyRecord && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }} data-testid="history-modal">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header bg-white border-bottom">
                <h5 className="modal-title font-weight-bold">
                  <i className="fa fa-clock-rotate-left mr-2 text-teal" />Record History — {historyRecord.name}
                </h5>
                <button type="button" className="close" onClick={() => setHistoryRecord(null)}><span>&times;</span></button>
              </div>
              <div className="modal-body p-4">
                <div className="d-flex align-items-center mb-3 p-3 bg-light rounded border">
                  <div style={{ flex: 1 }}>
                    <div className="small text-muted text-uppercase font-weight-600">Domain</div>
                    <div className="font-weight-bold">{historyRecord.domainLabel}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="small text-muted text-uppercase font-weight-600">Code</div>
                    <div className="font-weight-bold">{historyRecord.code}</div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div className="small text-muted text-uppercase font-weight-600">Status</div>
                    <div>{historyRecord.status === 'Active'
                      ? <span className="record-chip chip-live">Active</span>
                      : <span className="record-chip chip-muted">Inactive</span>}</div>
                  </div>
                </div>
                <p className="text-muted small text-center mt-4">
                  <i className="fa fa-info-circle mr-1" />Detailed change history will appear here once audit trail integration is enabled for this record type.
                </p>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setHistoryRecord(null)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function SystemSetupAndMasterDataPage() {
  const { hasRole } = useAuth();
  const canEdit = hasRole(...ADMIN_ROLESS);
  const [activeTab, setActiveTab] = useState('brand');
  const data = useMasterDataLists();

  // Counters surfaced in the header metric cards.
  const metrics = useMemo(() => ({
    brands: data.brands.filter((b) => b.is_active).length,
    outlets: data.outlets.filter((o) => o.is_active).length,
    items: data.items.filter((i) => i.is_active).length,
    users: data.assignments.filter((a) => a.is_active).length,
  }), [data]);

  return (
    <div className="container-fluid master-data-page py-4" data-testid="master-data-page">
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb bg-transparent px-0 py-0 mb-2">
              <li className="breadcrumb-item">Administration</li>
              <li className="breadcrumb-item active" aria-current="page">System Setup &amp; Master Data</li>
            </ol>
          </nav>
          <h1 className="page-title mb-1">System Setup &amp; Master Data</h1>
          <p className="text-muted mb-0">Configure foundational records before commissary operations begin.</p>
        </div>
      </div>

      {!canEdit && (
        <div className="alert alert-info" data-testid="readonly-notice">
          You are viewing master data in read-only mode. Only Super Admin or Finance can edit.
        </div>
      )}

      <div className="row metrics-row mb-4">
        {[
          { label: 'Active Brands', value: metrics.brands, testid: 'metric-brands' },
          { label: 'Outlet Mappings', value: metrics.outlets, testid: 'metric-outlets' },
          { label: 'Ingredient / SKU Records', value: metrics.items, testid: 'metric-items' },
          { label: 'Users Assigned', value: metrics.users, testid: 'metric-users' },
        ].map((m) => (
          <div key={m.testid} className="col-sm-6 col-xl-3 mb-3">
            <div className="card metric-card h-100">
              <div className="card-body">
                <span className="metric-label">{m.label}</span>
                <div className="metric-value" data-testid={m.testid}>{String(m.value).padStart(2, '0')}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body pb-2">
          <ul className="nav nav-pills nav-fill master-tabs" role="tablist" data-testid="master-tabs">
            {TABS.map((t) => (
              <li key={t.id} className="nav-item" role="presentation">
                <button
                  type="button"
                  className={`nav-link ${activeTab === t.id ? 'active' : ''}`}
                  onClick={() => { setActiveTab(t.id); window.scrollTo(0, 0); }}
                  data-testid={`tab-${t.id}`}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="tab-content">
        {activeTab === 'brand' && (
          <BrandOutletSection canEdit={canEdit} brands={data.brands} outlets={data.outlets} onChanged={data.refresh} />
        )}
        {activeTab === 'station' && (
          <StationSection canEdit={canEdit} onChanged={data.refresh} />
        )}
        {activeTab === 'item' && (
          <ItemSection canEdit={canEdit} items={data.items} onChanged={data.refresh} />
        )}
        {activeTab === 'supplier' && (
          <SupplierSection canEdit={canEdit} suppliers={data.suppliers} onChanged={data.refresh} />
        )}
        {activeTab === 'user' && (
          <div className="tab-pane fade show active" data-testid="tab-user-panel">
            <UserManagementSection canEdit={canEdit} mgmtUsers={data.mgmtUsers} onChanged={data.refresh} />
            <UserAssignmentSection
              canEdit={canEdit} users={data.users} roles={data.roles}
              brands={data.brands} outlets={data.outlets} assignments={data.assignments}
              onChanged={data.refresh}
            />
          </div>
        )}
        {activeTab === 'cutoff' && (
          <CutoffRuleSection canEdit={canEdit} brands={data.brands} cutoffs={data.cutoffs} onChanged={data.refresh} />
        )}
      </div>

      {/* Master Data Summary — always visible below tabs */}
      <div className="mt-4">
        <MasterDataSummary
          brands={data.brands}
          outlets={data.outlets}
          items={data.items}
          suppliers={data.suppliers}
          stations={data.stations}
          cutoffs={data.cutoffs}
          onChanged={data.refresh}
        />
      </div>
    </div>
  );
}
